import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import StarIcon from "@mui/icons-material/Star";
import axios from "axios";
import { useMemo, useRef, useState } from "react";
import base_url from "../../api/bootapi";
import {
  cacheProduct,
  formatMoney,
  getAllImages,
  getCoverImage,
  getCurrency,
  getDurationLabel,
  getFromPrice,
  getRating,
  getReviewCount,
  hasFreeCancellation,
} from "./experienceUtils";

export default function ExperienceProductCard({ product, onSelect }) {
  // Fall back to just the cover image when the gallery only has one shot -
  // the arrows/dots only render once there's actually something to slide to.
  const initialImages = useMemo(() => {
    const all = getAllImages(product, 500);
    if (all.length) return all;
    const cover = getCoverImage(product);
    return cover ? [cover] : [];
  }, [product]);

  const [images, setImages] = useState(initialImages);
  const [activeImage, setActiveImage] = useState(0);
  const fetchStateRef = useRef("idle"); // idle | loading | done

  // The search results only carry 1-2 photos per product. Rather than
  // firing a details request for every single card on page load (which
  // would mean dozens of parallel calls), we lazily fetch the fuller
  // Viator product-details payload the moment someone actually shows
  // interest in a single-image card (hover/focus), then swap the gallery
  // in. Cards that already came back with several photos skip this
  // entirely.
  // const enrichImagesIfNeeded = () => {
  //   if (fetchStateRef.current !== "idle") return;
  //   if (images.length > 1) return;
  //   const code = product && product.productCode;
  //   if (!code) return;

  //   fetchStateRef.current = "loading";
  //   axios
  //     .get(`${base_url.api1}/tourapp/viator/products/${code}`)
  //     .then((res) => {
  //       const payload = res && res.data;
  //       const fullProduct = payload && (payload.viator_response || payload.data || payload);
  //       if (!fullProduct || !fullProduct.images || !fullProduct.images.length) return;

  //       const merged = { ...product, ...fullProduct };
  //       cacheProduct(merged);

  //       const fullGallery = getAllImages(merged, 500);
  //       if (fullGallery.length > 1) setImages(fullGallery?.slice(0,6));
  //     })
  //     .catch(() => {
  //       // Keep showing the single cover image - nothing to fall back to.
  //     })
  //     .finally(() => {
  //       fetchStateRef.current = "done";
  //     });
  // };

  const rating = getRating(product);
  const reviewCount = getReviewCount(product);
  const duration = getDurationLabel(product);
  const price = getFromPrice(product);
  const currency = getCurrency(product);
  const canSlide = images.length > 1;

  // Every control that lives on top of the image needs to stop propagation,
  // otherwise a click on the arrows/dots also triggers onSelect and opens
  // the details page.
  const goPrev = (e) => {
    e.stopPropagation();
    setActiveImage((i) => (i === 0 ? images.length - 1 : i - 1));
  };
  const goNext = (e) => {
    e.stopPropagation();
    setActiveImage((i) => (i === images.length - 1 ? 0 : i + 1));
  };
  const goToDot = (e, idx) => {
    e.stopPropagation();
    setActiveImage(idx);
  };

  return (
    <div
      className="exp-product-card"
      role="button"
      tabIndex={0}
      onClick={() => onSelect(product)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect(product);
      }}
      // onMouseEnter={enrichImagesIfNeeded}
      // onFocus={enrichImagesIfNeeded}
    >
      <div className="exp-product-image">
        {images.length ? (
          <img src={images[activeImage]} alt={product.title} loading="lazy" />
        ) : (
          <div className="exp-product-image-placeholder" />
        )}

        {canSlide && (
          <>
            <button
              type="button"
              className="exp-card-arrow exp-card-arrow-prev swipe-g"
              onClick={goPrev}
              aria-label="Previous image"
            >
              <ChevronLeftIcon fontSize="small" />
            </button>
            <button
              type="button"
              className="exp-card-arrow exp-card-arrow-next swipe-g"
              onClick={goNext}
              aria-label="Next image"
            >
              <ChevronRightIcon fontSize="small" />
            </button>
            <div className="exp-card-dots swipe-g">
              {images.map((_, idx) => (
                <span
                  key={idx}
                  className={`exp-card-dot${idx === activeImage ? " exp-card-dot-active" : ""}`}
                  onClick={(e) => goToDot(e, idx)}
                />
              ))}
            </div>
          </>
        )}
      </div>
      <div className="exp-product-body">
        <h3 className="exp-product-title">{product.title}</h3>

        {(duration || hasFreeCancellation(product)) && (
          <div className="exp-product-subline">
            {duration}
            {duration && hasFreeCancellation(product) && <span className="exp-dot-sep">•</span>}
            {hasFreeCancellation(product) && "Free Cancellation"}
          </div>
        )}

        <div className="exp-product-bottom-row">
          {rating ? (
            <span className="exp-product-rating">
              <StarIcon fontSize="inherit" />
              {rating.toFixed(1)}
              {reviewCount ? ` (${reviewCount.toLocaleString()})` : ""}
            </span>
          ) : (
            <span />
          )}
          {price !== null && (
            <div className="exp-product-price">
              <span className="exp-price-label">from</span>
              <strong className="exp-price-value">{formatMoney(price, currency)}</strong>
              <span className="exp-price-per">per person</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
